import { useState } from 'react';
import { Button, Input, ShelfGrid } from '@cuito/ui';
import { readErrorMeta } from '@cuito/api-client';
import { shelvesApi } from '../../api/shelves';
import { readServerDetails, readServerMessage } from '../../lib/apiError';
import {
  describeAbsorbedShelves,
  describeBlockingShelves,
  describeConflicts,
  planMerge,
  selectionToRegion,
  toUiShelf,
  type GridCell,
  type MergePlan,
  type ShelfBox,
} from '../../lib/shelfForm';
import type { GridShelfView } from '../../api/shelves';

/** 空选择时的分流结果：没有区域就没有任何货架受影响 */
const NO_PLAN: MergePlan = { blocking: [], absorbed: [] };

interface Props {
  locationId: string;
  rows: number;
  cols: number;
  shelves: readonly ShelfBox[];
  /** 用于渲染网格底（含图标与商品数） */
  gridShelves: readonly GridShelfView[];
  onCreated: () => void;
  onCancel: () => void;
}

/**
 * 合并相邻格子创建货架（`04-api-contract.md` §7.2、`UC-SHELF-002`）。
 *
 * 三条硬约束决定这里的形状：
 *   1. 选择必须是实心矩形——`SHELF_NOT_RECTANGULAR` 是客户端校验（契约 §7.2 的说明），
 *      提交前拦下，不然用户只会收到一个结构上无法表达的请求；
 *   2. **不自己拼位置标识**：`code` 缺省交给服务端按位置生成（§7.2 校验表），
 *      05 §7.8 也明确「来自服务端下发的快照，客户端不自行拼接」；
 *   3. **阻塞判据只认「交叉」**：被新区域完整包住的 1×1 货架是服务端的**合并**语义
 *      （§7.2 副作用，`04-api-contract.md:1077-1081`），提示后照常放行；
 *      拿 `regionsOverlap` 直接判冲突会让这个按钮永远点不动（见 `lib/shelfForm.planMerge`）。
 *
 * 提示与阻塞两条都不是权威：服务端仍可能因为并发创建而回 409 `SHELF_OVERLAP`，
 * 所以 `submit()` 里保留了同一判据的兜底分支，catch 里也优先显示服务端原文。
 */
export default function ShelfMergeSelector({
  locationId,
  rows,
  cols,
  shelves,
  gridShelves,
  onCreated,
  onCancel,
}: Props) {
  const [selected, setSelected] = useState<GridCell[]>([]);
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selection = selectionToRegion(selected);
  // 契约镜像的分流：blocking 才禁用提交，absorbed 只提示（服务端的 409 仍是最终权威）
  const plan = selection.region === null ? NO_PLAN : planMerge(selection.region, shelves);

  const toggleCell = (row: number, col: number): void => {
    setSelected((prev) =>
      prev.some((cell) => cell.row === row && cell.col === col)
        ? prev.filter((cell) => !(cell.row === row && cell.col === col))
        : [...prev, { row, col }],
    );
  };

  const submit = async (): Promise<void> => {
    if (!selection.rectangular || selection.region === null) {
      setError(selection.error ?? '请选择矩形区域');
      return;
    }
    // 兜底：按钮已经按同一判据禁用，这里防的是「props 里的货架在两次渲染之间变了」
    if (plan.blocking.length > 0) {
      setError(describeBlockingShelves(plan.blocking));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await shelvesApi.createShelf(locationId, {
        ...selection.region,
        ...(label.trim() === '' ? {} : { label: label.trim() }),
      });
      setSelected([]);
      setLabel('');
      onCreated();
    } catch (e) {
      // 服务端是权威：SHELF_OVERLAP 的 conflicts 比前端预判更准（并发创建时前端看不到）
      const meta = readErrorMeta(e);
      setError(
        meta?.code === 'SHELF_OVERLAP'
          ? describeConflicts(readServerDetails(e))
          : (readServerMessage(e) ?? '上架失败，请重试'),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-gray-900">合并相邻格子</h2>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">
            {selection.region === null
              ? '已选 0 格'
              : `已选 ${selected.length} 格 → ${
                  selection.region.rowSpan
                } × ${selection.region.colSpan} 区域`}
          </span>
          <Button variant="secondary" onClick={onCancel} disabled={saving}>
            取消
          </Button>
        </div>
      </div>

      {error && (
        <p className="rounded-lg border-2 border-red-500 bg-red-50 p-2 text-sm text-red-600">
          {error}
        </p>
      )}

      {/*
        冲突必须**在选择时就显示**：按钮被禁用后 `submit()` 再也进不去，
        只在提交分支里报错等于把「为什么不能点」这条信息锁死在一个不可达的分支里。
      */}
      {plan.blocking.length > 0 && (
        <p data-testid="merge-blocking" className="rounded-lg border-2 border-red-500 bg-red-50 p-2 text-sm text-red-600">
          {describeBlockingShelves(plan.blocking)}
          （部分相交，或该货架跨度 ≥ 2；请先调整选择或拆分原货架）
        </p>
      )}

      {plan.absorbed.length > 0 && (
        <p data-testid="merge-absorbed" className="rounded-lg border-2 border-black bg-gray-50 p-2 text-sm">
          {describeAbsorbedShelves(plan.absorbed)}
        </p>
      )}

      {/* 网格必须处在有确定高度的容器里，`ResizeObserver` 才量得到（05 §2.4 约束 4） */}
      <div className="h-[60vh] w-full overflow-hidden rounded-lg border-2 border-black p-4">
        <ShelfGrid
          rows={rows}
          cols={cols}
          shelves={gridShelves.map(toUiShelf)}
          mode="manage"
          mergeSelection={{ selected, onToggleCell: toggleCell }}
        />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <Input
          label="展示名（选填，最多 32 字）"
          className="w-72"
          maxLength={32}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <Button
          onClick={() => void submit()}
          disabled={saving || !selection.rectangular || plan.blocking.length > 0}
          className="disabled:opacity-50"
        >
          {saving ? '创建中…' : '创建货架'}
        </Button>
      </div>

      <p className="text-sm text-gray-500">
        位置标识由服务端按位置生成（如 `A1-B2`），前端不自行拼接。
      </p>
    </section>
  );
}
